import { Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DeviceLogo, DeviceStatus, DeviceVersion } from '~/components/devices';
import { ActiveShowName } from '~/components/devices/ActiveShowName/ActiveShowName';
import { DeviceDisplayIndicator } from '~/components/devices/device-indicators/device-indicators';
import { AuthorizedLink } from '~/components/links';
import { ListRowHeadingValue } from '~/components/list-row';
import { MetaItem, SettingsBody, SettingsData, SettingsHead } from '~/components/settings';
import { useAppContext } from '~/contexts';
import { capitalize, formatDeviceKind } from '~/lib/string';
import { ValueWrapper } from '../lib';
import type { Device } from '../types';

interface DetailsProps {
  device: Device;
}

export const Details = ({ device }: DetailsProps) => {
  const { t } = useTranslation();
  const { currentNetwork, currentUser } = useAppContext();

  return (
    <>
      <SettingsHead>{t('pages.devices.show.details.quick_info')}</SettingsHead>
      <SettingsBody>
        <SettingsData className="last scroll no-space-between">
          <MetaItem className="first">
            <ListRowHeadingValue
              heading={t('pages.devices.show.details.connectivity')}
              value={<DeviceStatus device={device} displayText={true} />}
            />
          </MetaItem>
          <MetaItem>
            <ListRowHeadingValue
              heading={t('pages.devices.show.details.display')}
              value={
                <ValueWrapper>
                  <DeviceDisplayIndicator device={device} />
                  <Typography
                    variant="h6"
                    sx={{ color: device.displayOn ? 'success.main' : 'primary.dark' }}
                  >
                    {device.displayOn === true
                      ? t('pages.devices.show.details.display_on')
                      : device.displayOn === false
                      ? t('pages.devices.show.details.display_off')
                      : ''}
                  </Typography>
                </ValueWrapper>
              }
            />
          </MetaItem>
          <MetaItem>
            <ListRowHeadingValue
              heading={t('pages.devices.show.details.now_playing')}
              value={<ActiveShowName activeShow={device.activeShow} />}
            />
          </MetaItem>
          <MetaItem>
            <ListRowHeadingValue
              heading={t('common.labels.type')}
              value={
                <ValueWrapper sx={{ alignItems: 'center' }}>
                  <DeviceLogo kind={device.kind} />
                  <Typography variant="body1">{formatDeviceKind(device.kind)}</Typography>
                </ValueWrapper>
              }
            />
          </MetaItem>
          <MetaItem>
            <ListRowHeadingValue
              heading={t('pages.devices.show.details.arch')}
              value={device.arch}
            />
          </MetaItem>
          <MetaItem>
            <ListRowHeadingValue
              heading={t('common.labels.group')}
              value={
                <AuthorizedLink authorized={currentNetwork.canManage.value} entity={device.group} />
              }
            />
          </MetaItem>
          <MetaItem>
            <ListRowHeadingValue heading={t('common.labels.ip_address')} value={device.ipAddress} />
          </MetaItem>
          <MetaItem>
            <ListRowHeadingValue
              heading={t('pages.devices.show.details.mac_address')}
              value={device.macAddress}
            />
          </MetaItem>
          <MetaItem>
            <ListRowHeadingValue
              heading={t('common.labels.version')}
              value={<DeviceVersion device={device} />}
            />
          </MetaItem>
          <MetaItem className={currentUser.admin ? undefined : 'last'}>
            <ListRowHeadingValue
              heading={t('pages.devices.show.details.model')}
              value={device.model ?? '--'}
            />
          </MetaItem>
          {currentUser.admin && (
            <MetaItem className="last">
              <ListRowHeadingValue
                heading={t('pages.devices.show.details.display_server')}
                value={device.displayServer ? capitalize(device.displayServer) : '--'}
              />
            </MetaItem>
          )}
        </SettingsData>
      </SettingsBody>
    </>
  );
};
